import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { Card, CardContent, CardHeader, Icon } from "@sellar-io/components";
import dayjs from "dayjs";
import { useOrderDocumentList } from "../../../hooks/api/v2/orderDocument/useOrderDocumentList";
import { OrderDocumentListResponseType } from "@sellar-io/api-v2/types";
import { useOrderDocumentDownload } from "../../../hooks/api/v2/orderDocument/useOrderDocumentDownload";
import { downloadPDF } from "../../../utils/downloadHelpers";
import { useToast } from "../../toast/useToast.shared";
import { Toast } from "../../toast/Toast";
import { Dialog, DialogNavigation, DialogContent } from "../../modal/Dialog";
import { SpinnerLegacy } from "../..";
import { useState } from "react";
import { useCreditProcessorDownloadOrderInvoice } from "../../../hooks/api/v2/creditProcessor/useCreditProcessorDownloadOrderInvoice";

type OrderDocumentSummaryProps = {
  orderId: number;
  retailerIdOverride?: number;
  isTwoOrder: boolean;
};

type OrderDocument = OrderDocumentListResponseType[number];

export const OrderDocumentSummary = ({
  orderId,
  retailerIdOverride,
  isTwoOrder,
}: OrderDocumentSummaryProps) => {
  const { business } = useSellarCurrentBusiness();
  const { toast } = useToast();
  const [isDownloadDialogActive, setIsDownloadDialogActive] = useState(false);
  const isSupplier = !!business?.Supplier && !retailerIdOverride;
  const isBuyer = !!business?.Retailer || !!retailerIdOverride;
  const orderDocumentQuery = useOrderDocumentList({
    limit: 50,
    query: [
      isSupplier && {
        type: "SupplierId",
        modifier: "EQUALS",
        value: business.Supplier?.id,
      },
      isBuyer && {
        type: "RetailerId",
        modifier: "EQUALS",
        value: business?.Retailer?.id || retailerIdOverride,
      },
      { type: "OrderId", modifier: "EQUALS", value: orderId },
    ].filter(Boolean),
  });

  const orderDocumentDownloadMutation = useOrderDocumentDownload();
  const creditProcessorDownloadInvoiceMutation =
    useCreditProcessorDownloadOrderInvoice();

  const orderDocuments = (orderDocumentQuery.data?.pages[0] ?? []).sort(
    (a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix(),
  );

  if (
    !business ||
    orderDocumentQuery.isPending ||
    orderDocuments.length === 0
  ) {
    return null;
  }

  const handleDownloadSellarDocument = ({
    document,
  }: {
    document: OrderDocument;
  }) => {
    setIsDownloadDialogActive(true);

    orderDocumentDownloadMutation.mutate(
      {
        id: document.id,
        type: document.type,
      },
      {
        onSuccess: (data) => {
          const documentName =
            document.type === "credit-note" ? "Credit note" : "Invoice";
          const documentFileName = `${documentName} - ${document.customId} - ${dayjs(
            document.issuedAt ?? new Date(),
          ).format("DD-MM-YYYY")}.pdf`;

          downloadPDF(documentFileName, data.document);
          setIsDownloadDialogActive(false);
        },
        onError: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="ALERT_FILLED">
                Unable to download invoice at this time
              </Toast>
            ),
          });
          setIsDownloadDialogActive(false);
        },
      },
    );
  };

  const handleDownloadTwoDocument = ({ orderId }: { orderId: number }) => {
    setIsDownloadDialogActive(true);

    creditProcessorDownloadInvoiceMutation.mutate(
      {
        orderId,
      },
      {
        onSuccess: (data) => {
          const documentName = "Invoice";
          const documentFileName = `${documentName} - ${orderId}.pdf`;
          downloadPDF(documentFileName, data.document);
          setIsDownloadDialogActive(false);
        },
        onError: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="ALERT_FILLED">
                Unable to download invoice at this time
              </Toast>
            ),
          });
          setIsDownloadDialogActive(false);
        },
      },
    );
  };

  const handleDownloadDocument = ({
    document,
  }: {
    document: OrderDocument;
  }) => {
    if (isTwoOrder && isBuyer) {
      return handleDownloadTwoDocument({ orderId });
    }
    return handleDownloadSellarDocument({ document });
  };

  return (
    <>
      <Card className="bg-white">
        <CardHeader className="pb flex flex-col gap-2 bg-white p-4">
          <h5 id="orderDocuments" className="font-semibold text-zinc-800">
            Order documents
          </h5>
          <p className="text-zinc-500">View and download your documents here</p>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {orderDocuments.map((item) => (
              <OrderDocumentItem
                key={item.id}
                orderDocument={item}
                isTwoOrder={isTwoOrder}
                isBuyer={isBuyer}
                onDownload={handleDownloadDocument}
              />
            ))}
          </div>
        </CardContent>
      </Card>
      <DownloadDialog
        active={isDownloadDialogActive}
        onDismiss={() => setIsDownloadDialogActive(false)}
      />
    </>
  );
};

type OrderDocumentItemProps = {
  isTwoOrder: boolean;
  isBuyer: boolean;
  orderDocument: OrderDocument;
  onDownload: (params: { document: OrderDocument }) => void;
};

const getDocumentTitleText = (
  type: OrderDocumentListResponseType[number]["type"],
) => {
  if (["credit-note", "card-payment-with-deposit-credit-note"].includes(type)) {
    return "Credit note";
  }

  return "Invoice";
};

const OrderDocumentItem = ({
  orderDocument,
  isTwoOrder,
  isBuyer,
  onDownload,
}: OrderDocumentItemProps) => {
  const documentMessages = [
    orderDocument.voidedAt &&
      `Voided on ${dayjs(orderDocument.voidedAt).format("DD/MM/YYYY")}`,
    orderDocument.paidAt &&
      `Paid on ${dayjs(orderDocument.paidAt).format("DD/MM/YYYY")}`,
    orderDocument.issuedAt &&
      `Issued on ${dayjs(orderDocument.issuedAt).format("DD/MM/YYYY")}`,
    `Created on ${dayjs(orderDocument.createdAt).format("DD/MM/YYYY")}`,
  ].filter(Boolean);

  const documentTitleText = getDocumentTitleText(orderDocument.type);

  const documentIdentifier =
    isTwoOrder && isBuyer ? "" : orderDocument.customId;

  return (
    <div className="flex flex-1 items-center">
      <div className="flex flex-1 gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-200">
          <Icon size={1.2} icon="FILE_DOCUMENT_CHECK_OUTLINE" />
        </div>
        <div className="flex flex-col">
          <span className="font-semibold">
            {documentTitleText} {documentIdentifier}
          </span>
          <span>{documentMessages[0]}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Icon
          onClick={() => onDownload({ document: orderDocument })}
          icon="TRAY_ARROW_DOWN"
          className="cursor-pointer"
        />
      </div>
    </div>
  );
};

type DownloadDialogProps = {
  active: boolean;
  onDismiss: () => void;
};
export const DownloadDialog = ({ onDismiss, active }: DownloadDialogProps) => {
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
        title="Downloading order document"
      />
      <DialogContent>
        <SpinnerLegacy />
        <span>
          Download can take up to 10 seconds. Please do not navigate away until
          your download is complete.
        </span>
      </DialogContent>
    </Dialog>
  );
};
